'use client';

import React, { FC, useEffect, useLayoutEffect, useState } from 'react';

import { useTranslation } from '@/hooks/useTranslations';
import { useVisiblePathname } from '@/hooks/useVisiblePathname';

import Link from '../core/Link';
import './styles.scss';

export type Tab = {
  sectionID: string;
  label: string;
  active?: boolean;
  type?: 'link' | 'default';
};

interface TabNavigationProps {
  tabs?: Tab[];
}

const TabNavigation: FC<TabNavigationProps> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState<string | undefined>(
    tabs?.find((tab) => tab.active)?.sectionID
  );
  const { t } = useTranslation();
  const pathname = useVisiblePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onScrollToSection = (id: string) => {
    if (!document) return;
    const element = document.getElementById(id);
    if (!element) return;
    const elementPosition = element.getBoundingClientRect().top;
    const currentScroll = window.pageYOffset;
    const header = document.querySelector('.header') as HTMLElement | null;
    const headerHeight = header?.offsetHeight! + header?.scrollHeight! + 5 || 40;

    const offsetPosition = currentScroll + elementPosition - headerHeight;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
    setActiveTab(id);
  };

  const handleScroll = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setActiveTab(entry.target.id);
      }
    });
  };

  useEffect(() => {
    const active = tabs?.find((tab) => tab.active);
    setActiveTab(active?.sectionID);
  }, [pathname, tabs]);

  useEffect(() => {
    if (!pathname.includes('games')) {
      const timeoutId = setTimeout(() => {
        const observer = new IntersectionObserver(handleScroll, {
          threshold: 0.5,
        });

        tabs?.forEach((tab) => {
          const element = document.getElementById(tab.sectionID);
          if (element) observer.observe(element);
        });

        return () => {
          observer.disconnect();
        };
      }, 150);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [tabs]);

  if (!tabs?.length) {
    return null;
  }

  return (
    <div className="homSliderTabBlock">
      <div className="homSliderTabWrp">
        <div className="homSliderTabInner">
          {tabs?.map((tab: Tab, index) =>
            tab.type === 'link' ? (
              <Link
                key={index}
                href={tab.sectionID}
                className={`tabText ${activeTab === tab.sectionID ? 'active' : ''}`}
                id={tab.sectionID}
                onClick={() => {
                  if (pathname.includes('games')) {
                    localStorage.setItem('gamesCurrentPage', JSON.stringify(1));
                  }
                  setActiveTab(tab.sectionID);
                }}
              >
                {t(tab.label)}
              </Link>
            ) : (
              <span
                key={index}
                onClick={() => {
                  onScrollToSection(tab.sectionID);
                }}
                className={`tabText ${activeTab === tab.sectionID ? 'active' : ''}`}
              >
                {t(tab.label)}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TabNavigation;
